import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance with auth header
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('smj_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors.
//
// BUGFIX V19.12 — the previous version wiped localStorage and redirected to
// /login on ANY 401 response. That logged users out whenever a downstream
// dependency (notably QuickBooks Online) rejected our request and the
// backend propagated Intuit's 401 verbatim. Symptom: user signs in, the
// dashboard rehydrates the previously-selected QBO-connected client, QBO's
// refresh token is invalid_grant, backend returns 401, interceptor
// interprets it as "user session dead" and bounces them back to /login.
//
// Fix: only treat a 401 as a session-auth failure when it actually came
// from our session endpoints (/auth/me or /auth/login). Every other 401 is
// a domain-level error (e.g. expired QBO connection) and must bubble up to
// the calling component so it can show a useful message without nuking the
// user's session.
//
// V19.13 — Also surface a single-toast notification when a /qbo/* endpoint
// returns 502 with a "reconnect" hint. The 502 is V19.12's remapped
// upstream auth-failure from Intuit; users should know they need to
// reconnect rather than wondering why reports are blank. The toast is
// rate-limited per-(method+url) so a dashboard mounting 4 QBO calls in
// parallel produces at most one toast per endpoint, not a stack of four.
import { toast } from 'sonner';

const SESSION_AUTH_URLS = ['/auth/me', '/auth/login'];
const _qboReconnectToastShown = new Set();

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isSessionAuthCall = SESSION_AUTH_URLS.some(p => url.includes(p));
    if (status === 401 && isSessionAuthCall) {
      localStorage.removeItem('smj_token');
      localStorage.removeItem('smj_user');
      try { localStorage.removeItem('token'); } catch (e) { /* ignore */ }
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    } else if ((status === 424 || status === 502) && /\/qbo\//.test(url)) {
      // QBO upstream failure — likely an invalid_grant on the refresh token.
      // V25.6: the app now returns 424, because the ingress/Cloudflare
      // replaces the body of any 502/503/504 with its own error page and the
      // user never saw our message. 502 is kept here only for older builds.
      const detail = error.response?.data?.detail || '';
      if (/reconnect/i.test(detail)) {
        const key = `${error.config?.method || 'get'}:${url}`;
        if (!_qboReconnectToastShown.has(key)) {
          _qboReconnectToastShown.add(key);
          try {
            toast.error('QuickBooks reconnection required', {
              description: detail || 'The connection to QuickBooks needs to be re-authorized. Open the client and click "Connect QuickBooks".',
              duration: 8000,
              id: 'qbo-reconnect',  // sonner dedupes by id across renders
            });
          } catch (e) { /* sonner not mounted */ }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  verifyMFA: (email, code) => api.post('/auth/mfa/verify', { email, code }),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

// Clients API
export const clientsAPI = {
  list: () => api.get('/clients'),
  create: (data) => api.post('/clients', data),
  get: (id) => api.get(`/clients/${id}`),
  update: (id, data) => api.put(`/clients/${id}`, data),
  remove: (id) => api.delete(`/clients/${id}`),
};

// Import API
export const importAPI = {
  connectQBO: (clientId) => api.post(`/import/qbo/connect/${clientId}`),
  syncQBO: (clientId) => api.post(`/import/qbo/sync/${clientId}`),
  uploadQBD: (clientId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/import/qbd/upload/${clientId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getData: (clientId, period = '2024') => api.get(`/import/data/${clientId}?period=${period}`),
};

// QuickBooks Online API
export const qboAPI = {
  getAuthUrl: (clientId, redirectUri, frontendOrigin) => 
    api.get(`/qbo/authorize/${clientId}?redirect_uri=${encodeURIComponent(redirectUri)}&frontend_origin=${encodeURIComponent(frontendOrigin || '')}`),
  getStatus: (clientId) => api.get(`/qbo/status/${clientId}`),
  getAccounts: (clientId, periodEnd = null, periodStart = null) => {
    const params = new URLSearchParams();
    if (periodEnd) params.set('period_end', periodEnd);
    if (periodStart) params.set('period_start', periodStart);
    const qs = params.toString();
    return api.get(`/qbo/accounts/${clientId}${qs ? `?${qs}` : ''}`);
  },
  getTTM: (clientId, endDate = null) => {
    const qs = endDate ? `?end_date=${endDate}` : '';
    return api.get(`/qbo/ttm/${clientId}${qs}`);
  },
  getTrialBalance: (clientId, asOfDate) => 
    api.get(`/qbo/trial-balance/${clientId}${asOfDate ? `?as_of_date=${asOfDate}` : ''}`),
  refreshToken: (clientId) => api.post(`/qbo/refresh/${clientId}`),
  disconnect: (clientId) => api.post(`/qbo/disconnect/${clientId}`),
  connectionAudit: (clientId) => api.get(`/qbo/connection-audit/${clientId}`),
};

// AI Mapping API
export const aiAPI = {
  mapAccounts: (accounts, template) => api.post('/ai/map-accounts', { accounts, template }),
};

// Templates API
export const templatesAPI = {
  list: () => api.get('/templates'),
  create: (data) => api.post('/templates', data),
  get: (id) => api.get(`/templates/${id}`),
};

// Mappings API
export const mappingsAPI = {
  get: (clientId) => api.get(`/mappings/${clientId}`),
  health: (clientId) => api.get(`/mappings/${clientId}/health`),
  remapCandidates: (clientId) => api.get(`/mappings/${clientId}/remap-candidates`),
  remapByName: (clientId, source, dryRun) =>
    api.post(`/mappings/${clientId}/remap-by-name`, { source, dry_run: dryRun }),
  // Every save MUST declare its origin. The backend rejects writes
  // without a valid X-SMJ-Mapping-Origin header to prevent test/script
  // boundary violations against canonical client configuration data.
  // See /app/memory/mapping_forensics/POSTMORTEM.md.
  save: (clientId, mapping, tree = null, origin = 'staff-coa-editor') =>
    api.post(
      `/mappings/${clientId}`,
      { client_id: clientId, mapping, tree },
      { headers: { 'X-SMJ-Mapping-Origin': origin } },
    ),
};

// Reports API
export const reportsAPI = {
  list: (clientId) => api.get(`/reports/${clientId}`),
  generate: (data) => api.post('/reports/generate', data),
  sendToClient: (reportId) => api.post(`/reports/${reportId}/send`),
  getClientPortal: (clientId) => api.get(`/reports/client-portal/${clientId}`),
  getDetail: (reportId) => api.get(`/reports/detail/${reportId}`),
};

// Trend Data API
export const trendAPI = {
  getData: (clientId) => api.get(`/trend-data/${clientId}`),
};

// Audit Log API
export const auditAPI = {
  getLog: (limit = 100) => api.get(`/audit-log?limit=${limit}`),
};

export default api;
